import { useEffect, useMemo, useRef, useState } from "react";
import { buildLayout, DEMO_PROJECT, getSheetSize } from "@plaatplan/core";
import { localeTag } from "@plaatplan/i18n";
import { useLocale } from "../i18n/useLocale";

function rgba(hex, alpha) {
  const normalized = hex.replace("#", "");
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function pickPreviewSheet(sheets) {
  return [...sheets]
    .filter((sheet) => !sheet.unused && sheet.placements.length > 0)
    .sort((a, b) => b.placements.length - a.placements.length)[0] ?? sheets[0];
}

export function LandingPlanPreview() {
  const { display } = useLocale();
  const boardWrapRef = useRef(null);
  const [scale, setScale] = useState(0.14);
  const numberFormat = useMemo(
    () => new Intl.NumberFormat(localeTag(display.locale), { maximumFractionDigits: 0 }),
    [display.locale],
  );
  const percentFormat = useMemo(
    () => new Intl.NumberFormat(localeTag(display.locale), {
      maximumFractionDigits: 1,
      minimumFractionDigits: 0,
    }),
    [display.locale],
  );

  const { layout, sheet, sourcePanel } = useMemo(() => {
    const built = buildLayout(DEMO_PROJECT.sourcePanel, DEMO_PROJECT.projects);
    return {
      layout: built,
      sheet: pickPreviewSheet(built.sheets),
      sourcePanel: DEMO_PROJECT.sourcePanel,
    };
  }, []);

  const { length, width } = getSheetSize(sheet, sourcePanel);
  const sheetNumber = sheet.index + 1;

  useEffect(() => {
    const updateScale = () => {
      const available = (boardWrapRef.current?.clientWidth ?? 640) - 8;
      setScale(Math.min(available / length, 0.34));
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    if (boardWrapRef.current) observer.observe(boardWrapRef.current);
    return () => observer.disconnect();
  }, [length]);

  const { metrics } = layout;

  return (
    <section className="landing-example" aria-labelledby="landing-example-title">
      <header className="landing-example-head">
        <h2 id="landing-example-title">{display.t("web.landing.preview.title")}</h2>
        <p>
          {display.t("web.landing.preview.intro", {
            sheetNumber,
            length,
            width,
          })}
        </p>
      </header>

      <div className="landing-example-board-wrap" ref={boardWrapRef}>
        <div
          className="board landing-example-board"
          style={{ width: `${length * scale}px`, height: `${width * scale}px` }}
        >
          {sheet.placements.map((placement) => (
            <div
              className="placed-panel landing-example-piece"
              key={`${placement.panelId}-${placement.occurrence}`}
              style={{
                left: `${placement.x * scale}px`,
                top: `${placement.y * scale}px`,
                width: `${placement.length * scale}px`,
                height: `${placement.width * scale}px`,
                borderColor: placement.projectColor,
                background: rgba(placement.projectColor, 0.2),
              }}
            >
              <div className="piece-title">{placement.name}</div>
              <div className="piece-project" style={{ color: placement.projectColor }}>
                {placement.projectName}
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="landing-example-caption">
        {display.t("web.landing.preview.caption", {
          sheets: numberFormat.format(metrics.sheetCount),
          parts: numberFormat.format(metrics.partCount),
          utilisation: percentFormat.format(Math.min(metrics.utilisation, 100)),
        })}
      </p>
    </section>
  );
}
